const SolarPowerSystems = [
  {
    hideHeading: true,
  },
  {
    tag: 'h2',
    heading: 'How does solar power work?',
    description: `
    Put simply, solar power systems work by capturing light from the sun and
    converting it into electricity which can then be used in the home, with any
    excess energy exported to the grid for general use. Solar panels will need
    to be installed for this energy capture to take place. These panels
    generate direct current (DC) electricity which must be fed into a solar
    inverter to be converted to alternating current (AC) energy. Here’s a
    step-by-step guide to the process:
    <br/>
    <ol>
  <li> Sunlight hits the solar panels and generates electricity within its
    materials by knocking loose outer electrons. This produces DC electricity
    that cannot be used in the home.
    </li>
    <li> The DC electricity is directed to an inverter where it is converted to
    AC electricity.
    </li>
    <li> The inverter ensures that the property is provided with electricity
    directly from the solar system, only accessing energy from the grid when
    supply is running low. Any unused solar energy can be exported and used for
    a feed-in tariff.
    </li>
    <li> If you are exporting energy, your electricity company will install a
    bi-directional meter in your home to monitor incoming and outgoing energy
    supplies.
    </li>
    <li> Electricity bills will be calculated accordingly.</li>
    </ol>`,
  },
  {
    tag: 'h2',
    heading: 'What can solar power be used for?',
    description: `
   <h3>Powering your home</h3>
    As described above, solar energy represents an excellent way to power your
    entire home without necessarily needing to use energy from the grid. Of
    course, if you own a big home and use lots of energy, you will probably
    need to install more solar panels than average.
    <br/>
    <br/>
   <h3>Powering hot water systems</h3>
    Solar hot water systems use solar panels to collect heat from the sun which
    is then used to heat up water stored in a special cylinder. A boiler can be
    used as a backup if the solar system is unable to heat the water to your
    desired temperature.
    <br/>
    <br/>
<h3>Heating pools</h3>
    A pool can be heated using solar power with the use of a solar collector, a
    filter, a pump, and a control panel. The process involves passing the water
    through the pump and filter into the solar collector, where it is heated by
    the sun before flowing back into the pool. Special sensors in the pool can
    help to control how high the temperature in the pool gets. One of the best
    things about heating your pool with a solar system is that it costs almost
    nothing. What’s more, solar panels have very long life expectancies of over
    three decades, meaning that you can rest assured your pool will be
    adequately heated for years to come.
    <br/>
    <br/>
  <h3>Outdoor and indoor lighting</h3>
    Outdoor and indoor solar lighting represents a cost-effective and
    aesthetically pleasing addition to your home. As well as being relatively
    cheap compared to other energy options, solar power is very efficient for
    lights, promising to illuminate your garden, patio, or living room all
    night long if necessary.
    `,
  },
  {
    tag: 'h2',
    heading: `Do I need to hire a professional to install a solar power system?
    `,
    description: `It is not absolutely necessary to hire a professional to install your solar
    power system. However, it is very wise. Here’s why:<br>
<ul>
    <li>
        Solar systems are complex and can be difficult to set up. If you want
        to do it yourself, you will need to have a good working knowledge of
        construction processes and electrical systems. Indeed, as well as
        purchasing all of the equipment for yourself, you will need to
        correctly connect the solar panels and inverter, wiring the latter to a
        meter or battery. As well as being difficult, this can be downright
        dangerous for those without the requisite experience.
    </li>
    <li>
        Most utility companies require homeowners to obtain special permits to
        connect solar systems to the grid, which can involve complex processes.
        Going off-grid is not advised as you may not be able to meet your
        energy requirements alone.
    </li>
    <li>
        By opting for a professional, you can rest assured that all of the
        relevant permits and parts have been purchased before the solar system
        is installed. An installer will also be able to determine how much
        energy your home will require.
    </li>
    <li>
        Although professional solar installation is more expensive than a DIY
        job, professional installers tend to have access to some of the best
        equipment on the market, meaning your solar system will be as efficient
        as possible.
    </li>
</ul>`,
  },
  {
    tag: 'h2',
    heading: `FAQ about solar power systems in Australia`,
    faqs: [
      {
        tag: 'h3',
        q: `What are the benefits of installing a solar power system?`,
        a: `The solar industry is booming because of its array of excellent benefits. These include:<br><br>
       <h4>Reducing your energy bills</h4>
       <br>
Although it may seem a little pricey to install solar panels on your home or business, any costs incurred will quickly be made up by the amount of money you save on energy bills. Remember, those utility companies who rely on oil, coal and natural gas to provide energy to their customers are at the mercy of the fluctuating fossil fuel market, often hiking up prices when times get tough. With solar panels, you will be freed from high monthly charges and will only need to pay for energy from the grid when your solar panels have not quite generated enough energy to meet your needs.
<br><br>
Fortunately, Australia is one of the best countries to be when it comes to solar power generation. Most solar system owners in the country actually find that they produce more energy than they need which can be sold off for financial gain. In this way, homeowners or businesses who opt for solar power can save themselves huge sums of money in the long term.
<br><br>
If that weren’t enough, solar panels are also very hard-wearing and require very little costly maintenance. Some solar panel businesses even hire their very own repair teams who will service customers’ solar systems on a regular basis.
<br><br>
<h4>Solar power is better for the environment</h4>
<br>
Generating energy with a solar energy system rather than relying on fossil fuels can significantly reduce the amount of greenhouse gas you release into the atmosphere. Greenhouse gases play a major role in increasing temperatures across the globe and contribute to destructive climate change. Already, climate change is having a marked effect on people’s lives across the world, with ecosystem changes, rising sea levels and extreme weather events causing disease, forced migration, the destruction of homes, drought and even death. By making simple changes to your life such as opting for solar power, you can limit your carbon footprint and help do your bit for the environment.
<br><br>
<h4>Solar power can increase the value of your home</h4>
<br>
Solar panels can significantly increase the value of your home. Potential buyers are likely to be enticed by the prospect of enjoying a solar system without having to go through the purchase and installation process themselves. What’s more, solar panels are actually incredibly aesthetically pleasing, giving properties a sleek and modern look that will attract all kinds of buyers.        `,
      },
      {
        tag: 'h3',
        q: `How much does installing solar panels cost?`,
        a: `A high quality 3kW solar system will cost the average Australian between $5,000 and $8,000 depending on the quality of the materials used. You will also need to take into account the price of extra technologies such as additional equipment to run your hot water system and the cost of installation.`,
      },
      {
        tag: 'h3',
        q: `What is included in a solar power system?`,
        a: `Solar panel systems tend to include the following elements:<br><br>
       <h4>Panels</h4>
       <br>
       These are the most expensive part of the solar system, as well as the most fundamental as they help to capture the light needed to generate energy. Panels are made of PV cells that are nestled in between several layers of semiconducting materials including silicone.<br><br>
<h4>Inverter</h4>
<br>
The inverter is used to convert the DC generated by your solar panels into AC energy that can be used in the home or fed into a commercial electrical grid.<br><br>
<h4>Bi-directional meter</h4>
<br>
A bi-directional meter measures how much electricity you draw from or feed into the grid. You will be charged or paid depending on whether you have used energy from the grid to make up for deficits or fed excess electricity into the grid.
<br><br>
<h4>Mounting system</h4>
<br>
A mounting system is used to fix solar panels to surfaces including building facades, roofs, or simply the ground.
 `,
      },
      {
        tag: 'h3',
        q: `Why is cleaning your solar panels important?`,
        a: `Solar panels can only work if sunlight is allowed to penetrate the solar cells. The more light that hits each panel, the more power will be generated for your home or business. Due to the fact that solar panels are usually tilted upwards towards the sun, they are susceptible to being dirtied by dirt, dust, and bird droppings. These substances can reduce the amount of light that hits your solar panels and reduce their efficiency.
<br><br>
        In this way, it is important to clean solar panels to ensure they work at full capacity all year round. Don’t rely on the rain to wash off the muck – only thorough cleaning will ensure that they are spotless.
        `,
      },
      {
        tag: 'h3',
        q: `How often should you clean solar panels?`,
        a: `Most solar panel companies recommend conducting a thorough clean every six months. It may be worth hiring a professional for the job to ensure it is done well.`,
      },
      {
        tag: 'h3',
        q: `What can impact how much solar energy my panels receive?`,
        a: `<h4>Panel placement and orientation</h4>
        <br>
        It is imperative that you place your panels somewhere that sunlight can easily reach them. This is why most homeowners opt to place their panels on their roofs or on the ground, tilted upwards.
        <br><br>
       <h4>Weather</h4>
       <br>
        Unsurprisingly, the more sun energy is able to reach your solar panels, the more energy they will be able to produce. This means that the warmer, brighter day of summer will help to power your home or business more efficiently.
        <br><br>
        <h4>System capacity</h4>
        <br>
        Different solar systems have different energy capacities. If you own a big home or property you will need to install a solar system with enough capacity to meet your energy requirements. As such, it is a good idea to ask a solar provider ‘How many solar panels do I need?’ before going ahead with the installation.
        <br><br>
        <h4>Shade or obstructions</h4>
        <br>
        Shade and shadows can impair the efficiency of your system, even if you own the best solar panels on the market. In this way, it is a good idea to move your panels away from any large buildings or obstructions that may cast looming shadows.
        `,
      },
      {
        tag: 'h3',
        q: `Do solar powered lights work at night?`,
        a: `Yes. Solar battery storage is such that your panels do not need to be constantly working in order to provide electricity.`,
      },
      {
        tag: 'h3',
        q: `What direction should solar panels face?`,
        a: `As Australia is in the southern hemisphere, you should ideally place your solar panels so that they face north. This will ensure the best power yield.`,
      },
      {
        tag: 'h3',
        q: `Will my solar panels be covered by warranty?`,
        a: `Yes. Most reputable solar panel companies offer generous warranties that should help to protect your finances from any adverse events.`,
      },
      {
        tag: 'h3',
        q: `How can I find the best solar power installers near me?`,
        a: `<h4>Request and compare quotes on Localsearch</h4>
        <br>
        Use Localsearch to request and compare quotes to purchase the best energy systems and installation services for you.
        <br><br>
        <h4>Compare reviews</h4>
        <br>
        Localsearch will help you to quickly compare reviews for solar installers and solar panel efficiency, ensuring that you hire the most reliable professionals in the business.
        `,
      },
    ],
  },
];

export default SolarPowerSystems;
